import { collection, deleteDoc, doc, onSnapshot, query, setDoc, updateDoc, writeBatch } from 'firebase/firestore';
import React, { useEffect, useMemo, useState } from 'react'
import { db } from '../firebaseConfig';
import { Button } from '@mui/material';
import { Link } from 'react-router-dom';
import './admin.css';

const TRIVIA_DOC = "s5y7fVw6BM5iOZs75VdM";
const SCORECARD_DOC = "MQyyHk4Q8g1UonUg7ZNc";
const CURRENT_QUESTION_DOC = "UEsaFssIIvcTBHM9hejH";

const blankQuestionForm = {
  docId: "",
  sequence: "",
  text: "",
  correctAnswerId: "1",
  answers: [
    { id: "1", text: "" },
    { id: "2", text: "" },
    { id: "3", text: "" },
    { id: "4", text: "" },
  ],
};

function parseQuestion(rawText) {
  try {
    const data = JSON.parse(rawText || "{}");
    const answers = Array.isArray(data.answers) ? data.answers : [];

    return {
      text: data.text || "",
      correctAnswerId: String(data.correctAnswerId || "1"),
      answers: [0, 1, 2, 3].map((index) => ({
        id: String(answers[index]?.id || index + 1),
        text: answers[index]?.text || "",
      })),
    };
  } catch {
    return {
      text: "Unable to read question JSON",
      correctAnswerId: "1",
      answers: blankQuestionForm.answers,
      hasError: true,
    };
  }
}

function toQuestionForm(question) {
  const parsed = parseQuestion(question.text);
  return {
    docId: question.id,
    sequence: question.sequence || "",
    text: parsed.text,
    correctAnswerId: parsed.correctAnswerId,
    answers: parsed.answers,
  };
}

function buildQuestionPayload(form) {
  return {
    sequence: Number(form.sequence) || 0,
    text: JSON.stringify({
      text: form.text.trim(),
      correctAnswerId: String(form.correctAnswerId),
      answers: form.answers.map((answer, index) => ({
        id: String(answer.id || index + 1),
        text: answer.text.trim(),
      })),
    }),
  };
}

function getGuestScore(guest, questions) {
  if (!Array.isArray(guest.answers)) return 0;

  return questions.reduce((score, question) => {
    const parsed = parseQuestion(question.text);
    const answer = guest.answers.find((item) => item.questionId === question.id);
    return answer && parsed.correctAnswerId === String(answer.answerId) ? score + 1 : score;
  }, 0);
}

const AdminPanel = () => {
  const [questions, setQuestions] = useState([])
  const [guests, setGuests] = useState([])
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [isTriviaOn, setIsTriviaOn] = useState(false);
  const [isShowScorecard, setIsShowScorecard] = useState(false);
  const [form, setForm] = useState(blankQuestionForm);
  const [saveMessage, setSaveMessage] = useState("");
  const [draggedQuestionId, setDraggedQuestionId] = useState("");
  const [mainTab, setMainTab] = useState("questions");
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);

  useEffect(() => {
    const unsubScorecard = onSnapshot(doc(db, "isShowScorecard", SCORECARD_DOC), (snapshot) => {
      setIsShowScorecard(Boolean(snapshot.data()?.isShowScorecard))
    });
    const unsubTrivia = onSnapshot(doc(db, "isTriviaOn", TRIVIA_DOC), (snapshot) => {
      setIsTriviaOn(Boolean(snapshot.data()?.isOn))
    });
    const unsubCurrentQuestion = onSnapshot(doc(db, "currentQuestion", CURRENT_QUESTION_DOC), (snapshot) => {
      setCurrentQuestion(snapshot.data()?.id || "")
    });

    return () => {
      unsubScorecard();
      unsubTrivia();
      unsubCurrentQuestion();
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(query(collection(db, 'question')), (snapshot) => {
      const updatedQuestions = snapshot.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .sort((a, b) => (Number(a.sequence) || 0) - (Number(b.sequence) || 0));

      setQuestions(updatedQuestions);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(query(collection(db, 'guest')), (snapshot) => {
      const updatedGuests = snapshot.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .sort((a, b) => (a.name || "Anonymous").localeCompare(b.name || "Anonymous"));

      setGuests(updatedGuests);
    });

    return () => unsubscribe();
  }, []);

  const scores = useMemo(() => {
    return guests
      .map((guest) => ({
        id: guest.id,
        name: guest.name || "Anonymous",
        score: getGuestScore(guest, questions),
        answered: Array.isArray(guest.answers) ? guest.answers.length : 0,
      }))
      .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
  }, [guests, questions]);

  const selectedQuestion = questions.find((question) => question.id === currentQuestion);

  function updateGameFlag(collectionName, documentId, payload) {
    return updateDoc(doc(db, collectionName, documentId), payload).catch((error) => {
      console.error('Admin update failed:', error);
    });
  }

  function setShowScorecard(flag) {
    updateGameFlag('isShowScorecard', SCORECARD_DOC, { isShowScorecard: flag });
    if (flag) {
      setTrivia(false);
      changeCurrentQuestion("");
    }
  }

  function setTrivia(flag) {
    updateGameFlag('isTriviaOn', TRIVIA_DOC, { isOn: flag });
    changeCurrentQuestion("");
    if (flag) {
      setShowScorecard(false);
    }
  }

  function changeCurrentQuestion(id) {
    updateGameFlag('currentQuestion', CURRENT_QUESTION_DOC, { id });
  }

  function showQuestion(id) {
    updateGameFlag('isShowScorecard', SCORECARD_DOC, { isShowScorecard: false });
    updateGameFlag('isTriviaOn', TRIVIA_DOC, { isOn: true });
    changeCurrentQuestion(id);
  }

  function handleFormField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function handleAnswerChange(index, value) {
    setForm((current) => ({
      ...current,
      answers: current.answers.map((answer, answerIndex) => (
        answerIndex === index ? { ...answer, text: value } : answer
      )),
    }));
  }

  function startNewQuestion() {
    const nextSequence = questions.length
      ? Math.max(...questions.map((question) => Number(question.sequence) || 0)) + 1
      : 1;

    setForm({
      ...blankQuestionForm,
      sequence: nextSequence,
    });
    setSaveMessage("");
    setIsQuestionModalOpen(true);
  }

  function editQuestion(question) {
    setForm(toQuestionForm(question));
    setSaveMessage("");
    setIsQuestionModalOpen(true);
  }

  function closeQuestionModal() {
    setIsQuestionModalOpen(false);
    setSaveMessage("");
  }

  async function saveQuestionOrder(orderedQuestions) {
    const batch = writeBatch(db);

    orderedQuestions.forEach((question, index) => {
      batch.update(doc(db, 'question', question.id), { sequence: index + 1 });
    });

    await batch.commit();
  }

  async function moveQuestion(droppedQuestionId) {
    if (!draggedQuestionId || draggedQuestionId === droppedQuestionId) return;

    const fromIndex = questions.findIndex((question) => question.id === draggedQuestionId);
    const toIndex = questions.findIndex((question) => question.id === droppedQuestionId);

    if (fromIndex === -1 || toIndex === -1) return;

    const reorderedQuestions = [...questions];
    const [movedQuestion] = reorderedQuestions.splice(fromIndex, 1);
    reorderedQuestions.splice(toIndex, 0, movedQuestion);

    setQuestions(reorderedQuestions.map((question, index) => ({ ...question, sequence: index + 1 })));
    setDraggedQuestionId("");
    await saveQuestionOrder(reorderedQuestions);
  }

  async function saveQuestion(event) {
    event.preventDefault();
    setSaveMessage("");

    if (!form.text.trim()) {
      setSaveMessage("Question text is required.");
      return;
    }

    const hasBlankAnswer = form.answers.some((answer) => !answer.text.trim());
    if (hasBlankAnswer) {
      setSaveMessage("All four answer options are required.");
      return;
    }

    const payload = buildQuestionPayload(form);
    const ref = form.docId ? doc(db, 'question', form.docId) : doc(collection(db, 'question'));

    await setDoc(ref, payload, { merge: Boolean(form.docId) });
    setForm((current) => ({ ...current, docId: ref.id }));
    setSaveMessage(form.docId ? "Question updated." : "Question added.");
    setIsQuestionModalOpen(false);
  }

  async function deleteQuestion(question) {
    await deleteDoc(doc(db, 'question', question.id));
    await saveQuestionOrder(questions.filter((item) => item.id !== question.id));
    if (currentQuestion === question.id) {
      changeCurrentQuestion("");
    }
    if (form.docId === question.id) {
      startNewQuestion();
    }
  }

  async function deleteParticipant(participant) {
    await deleteDoc(doc(db, 'guest', participant.id));
  }

  return (
    <div className="admin-shell">
      <div className="admin-header">
        <div>
          <h1 className="admin-title">Are we there yet?</h1>
          <p className="admin-subtitle">Caltrans History Trivia Ride host console.</p>
        </div>
        <div className="admin-status-bar">
          <span className={`admin-pill ${isTriviaOn ? 'is-live' : ''}`}>Trivia {isTriviaOn ? 'Live' : 'Off'}</span>
          <span className={`admin-pill ${isShowScorecard ? 'is-live' : ''}`}>Scorecard {isShowScorecard ? 'Visible' : 'Hidden'}</span>
        </div>
      </div>

      <div className="admin-tabs admin-main-tabs" role="tablist" aria-label="Admin sections">
        <button
          aria-selected={mainTab === "questions"}
          className={`admin-tab ${mainTab === "questions" ? "is-active" : ""}`}
          onClick={() => setMainTab("questions")}
          role="tab"
          type="button"
        >
          Questions
        </button>
        <button
          aria-selected={mainTab === "scorecard"}
          className={`admin-tab ${mainTab === "scorecard" ? "is-active" : ""}`}
          onClick={() => setMainTab("scorecard")}
          role="tab"
          type="button"
        >
          Scorecard
        </button>
        <button
          aria-selected={mainTab === "participants"}
          className={`admin-tab ${mainTab === "participants" ? "is-active" : ""}`}
          onClick={() => setMainTab("participants")}
          role="tab"
          type="button"
        >
          Participants
        </button>
      </div>

      <div className="admin-grid">
        <div className="admin-stack">
          <section className="admin-section">
            <h2>Host Controls</h2>
            <div className="admin-actions">
              <Button variant={isTriviaOn ? 'contained' : 'outlined'} onClick={() => setTrivia(!isTriviaOn)}>
                {isTriviaOn ? 'Stop Trivia' : 'Start Trivia'}
              </Button>
              <Button variant={isShowScorecard ? 'contained' : 'outlined'} onClick={() => setShowScorecard(!isShowScorecard)}>
                {isShowScorecard ? 'Hide Scorecard' : 'Show Scorecard'}
              </Button>
            </div>
            <p className="admin-muted">
              Current question: {selectedQuestion ? parseQuestion(selectedQuestion.text).text : "None selected"}
            </p>
          </section>

        </div>

        <div className="admin-stack">
          {mainTab === "questions" && <section className="admin-section" role="tabpanel">
            <div className="admin-section-header">
              <h2>Questions</h2>
              <Button type="button" variant="contained" onClick={startNewQuestion}>Add Question</Button>
            </div>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Move</th>
                    <th>Question</th>
                    <th>Correct</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {questions.map((question) => {
                    const parsed = parseQuestion(question.text);
                    const correct = parsed.answers.find((answer) => answer.id === parsed.correctAnswerId);
                    return (
                      <tr
                        className={`${currentQuestion === question.id ? 'is-current' : ''} ${draggedQuestionId === question.id ? 'is-dragging' : ''}`}
                        draggable
                        key={question.id}
                        onDragEnd={() => setDraggedQuestionId("")}
                        onDragOver={(event) => event.preventDefault()}
                        onDragStart={() => setDraggedQuestionId(question.id)}
                        onDrop={() => moveQuestion(question.id)}
                      >
                        <td><span className="admin-drag-handle">Drag</span></td>
                        <td className="admin-question-text">{parsed.text}</td>
                        <td>{correct?.text || 'Not set'}</td>
                        <td>
                          <div className="admin-actions">
                            <Button size="small" variant="contained" onClick={() => showQuestion(question.id)}>Show</Button>
                            <Button size="small" variant="outlined" onClick={() => editQuestion(question)}>Edit</Button>
                            <Button size="small" color="error" variant="outlined" onClick={() => deleteQuestion(question)}>Delete</Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>}

          {mainTab === "scorecard" && (
            <section className="admin-section" role="tabpanel">
              <h2>Scorecard</h2>
              <div className="admin-tab-panel">
                {scores.length === 0 && <p className="admin-muted">No participant scores yet.</p>}
                <div className="admin-score-list">
                  {scores.slice(0, 8).map((score, index) => (
                    <div className="admin-score" key={score.id}>
                      #{index + 1} {score.name}
                      <strong>{score.score}</strong>
                      <span>{score.answered}/{questions.length} answered</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {mainTab === "participants" && (
            <section className="admin-section" role="tabpanel">
              <h2>Participants</h2>
              <div className="admin-tab-panel">
                <div className="admin-actions" style={{ marginBottom: 12 }}>
                  <Link to="/admin-add-guest">Add participant</Link>
                </div>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Score</th>
                        <th>Answered</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scores.map((score) => {
                        const guest = guests.find((item) => item.id === score.id) || {};
                        return (
                          <tr key={score.id}>
                            <td>{score.name}</td>
                            <td>{score.score}</td>
                            <td>{score.answered}/{questions.length}</td>
                            <td>
                              <div className="admin-actions">
                                <Link to={`/admin-guest-edit/${score.id}`}>Edit</Link>
                                <Link to={`/guest-access/${score.id}`}>Open player</Link>
                                <Button size="small" color="error" variant="outlined" onClick={() => deleteParticipant(guest)}>Delete</Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
      {isQuestionModalOpen && (
        <div className="admin-modal-backdrop" role="presentation" onMouseDown={closeQuestionModal}>
          <div
            aria-modal="true"
            className="admin-modal"
            onMouseDown={(event) => event.stopPropagation()}
            role="dialog"
          >
            <div className="admin-modal-header">
              <h2>{form.docId ? 'Edit Question' : 'Add Question'}</h2>
              <button className="admin-modal-close" onClick={closeQuestionModal} type="button">Close</button>
            </div>
            <form className="admin-form" onSubmit={saveQuestion}>
              <div className="admin-field">
                <label htmlFor="questionText">Question</label>
                <textarea
                  autoFocus
                  className="admin-textarea"
                  id="questionText"
                  value={form.text}
                  onChange={(event) => handleFormField('text', event.target.value)}
                />
              </div>
              <div className="admin-options">
                {form.answers.map((answer, index) => (
                  <div className="admin-option-row" key={answer.id}>
                    <span>#{answer.id}</span>
                    <input
                      className="admin-input"
                      value={answer.text}
                      placeholder={`Option ${index + 1}`}
                      onChange={(event) => handleAnswerChange(index, event.target.value)}
                    />
                    <label className="admin-radio-label">
                      <input
                        checked={form.correctAnswerId === answer.id}
                        name="correctAnswer"
                        type="radio"
                        value={answer.id}
                        onChange={(event) => handleFormField('correctAnswerId', event.target.value)}
                      />
                      Correct
                    </label>
                  </div>
                ))}
              </div>
              {saveMessage && <div className="admin-muted">{saveMessage}</div>}
              <div className="admin-actions">
                <Button type="submit" variant="contained">{form.docId ? 'Save Changes' : 'Add Question'}</Button>
                {form.docId && <Button type="button" variant="outlined" onClick={() => setForm({ ...form, docId: "" })}>Duplicate</Button>}
                <Button type="button" variant="outlined" onClick={closeQuestionModal}>Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminPanel
